import React, { useEffect, useRef, useState } from 'react';

interface ScrollRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
}

export const ScrollReveal: React.FC<ScrollRevealProps> = ({
  children,
  className = '',
  delay = 0,
  direction = 'up',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (ref.current) observer.unobserve(ref.current);
        }
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  const getInitialTransform = () => {
    switch (direction) {
      case 'up':
        return 'translate-y-8 sm:translate-y-12';
      case 'down':
        return '-translate-y-8 sm:-translate-y-12';
      case 'left':
        return 'translate-y-6 sm:translate-y-0 sm:translate-x-12';
      case 'right':
        return 'translate-y-6 sm:translate-y-0 sm:-translate-x-12';
      case 'none':
        return 'translate-y-0 scale-95';
      default:
        return 'translate-y-8 sm:translate-y-12';
    }
  };

  // Convert delay: if < 10 treat as seconds (e.g. 0.1 -> 100ms), else treat as ms
  const computedDelayMs = delay < 10 ? delay * 1000 : delay;

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${computedDelayMs}ms` }}
      className={`transition-all duration-800 cubic-bezier(0.16, 1, 0.3, 1) transform will-change-transform ${
        isVisible
          ? 'opacity-100 translate-y-0 translate-x-0 scale-100'
          : `opacity-0 ${getInitialTransform()}`
      } ${className}`}
    >
      {children}
    </div>
  );
};
